import { PUDDLE_COLOURS } from "./puddle-view.ts";

/**
 * The look of a drip puddle under a Drenched character (ADR-144's amendment of 2026-11-01, decision 8): a small, roundish
 * pool painted a pixel at a time in the rain's puddle colours, translucent so the floor shows through, deeper towards the
 * middle, with a soft edge, a sheen along the side that faces the sky, a few glints and a darker halo of wet floor. It starts as one small
 * ellipse under the feet; two smaller lobes join it only in the second half of its growth, so it stays round while small.
 * Pure: it writes into the caller's pixels and allocates nothing.
 */

/** The width of a full drip puddle's main ellipse, in world pixels. */
export const DRIP_PUDDLE_PX = 18;

/** The main ellipse's height over its width, for a pool seen from above at the game's angle. */
const FLAT = 0.62;
const EDGE = 0.3;
const HALO = 0.45;
const HALO_ALPHA = 64;
const WATER_ALPHA = 120;
const CORE_ALPHA = 150;
const SHEEN_ALPHA = 170;
const HASH = 0x9e3779b1;

/**
 * A hashed byte for a pixel of a puddle.
 *
 * @param seed The puddle's seed.
 * @param x The pixel's column.
 * @param y The pixel's row.
 * @returns 0 to 255.
 */
function roll(seed: number, x: number, y: number): number {
  return Math.imul((x + 7) ^ Math.imul(y + 13, 0x27d4eb2d) ^ Math.imul(seed + 1, HASH), HASH) >>> 24;
}

/**
 * The field of a drip puddle at a pixel: above 0 inside the water, 1 at the middle of the main ellipse.
 *
 * @param seed The puddle's seed.
 * @param growth 0 just started to 1 full.
 * @param dx The pixel's offset from the feet along x.
 * @param dy Its offset along y.
 * @returns The field.
 */
export function dripField(seed: number, growth: number, dx: number, dy: number): number {
  const g = Math.max(0, Math.min(1, growth));
  const rx = 1.5 + (DRIP_PUDDLE_PX / 2 - 1.5) * g;
  const ry = Math.max(1, rx * FLAT);
  const ex = dx / rx;
  const ey = dy / ry;
  let best = 1 - ex * ex - ey * ey;
  const lobes = Math.max(0, g - 0.5) * 2;
  if (lobes > 0) {
    const side = (seed & 1) === 0 ? 1 : -1;
    const lx = (dx - side * rx * 0.75) / (rx * 0.5 * lobes + 0.01);
    const ly = (dy - ry * 0.25) / (ry * 0.6 * lobes + 0.01);
    best = Math.max(best, 1 - lx * lx - ly * ly);
    const ox = (dx + side * rx * 0.55) / (rx * 0.4 * lobes + 0.01);
    const oy = (dy + ry * 0.2) / (ry * 0.5 * lobes + 0.01);
    best = Math.max(best, 1 - ox * ox - oy * oy);
  }
  return best + ((roll(seed, Math.floor(dx / 2), Math.floor(dy / 2)) / 255) - 0.5) * 0.18;
}

/**
 * Paints a drip puddle into pixels, its feet at the middle.
 *
 * @param pixels The pixels, cleared first; a square side wide enough for `DRIP_PUDDLE_PX` and its halo.
 * @param seed The puddle's seed.
 * @param growth 0 just started to 1 full; 0 or less paints nothing.
 */
export function paintDripInto(pixels: ImageData, seed: number, growth: number): void {
  const data = pixels.data;
  const side = pixels.width;
  const middle = side / 2;
  data.fill(0);
  if (growth <= 0) {
    return;
  }
  const { deep, sheen, streak, rim } = PUDDLE_COLOURS;
  for (let y = 0; y < pixels.height; y += 1) {
    for (let x = 0; x < side; x += 1) {
      const dx = x + 0.5 - middle;
      const dy = y + 0.5 - middle;
      const v = dripField(seed, growth, dx, dy);
      if (v <= -HALO) {
        continue;
      }
      const at = (y * side + x) * 4;
      if (v <= 0) {
        data[at] = rim[0];
        data[at + 1] = rim[1];
        data[at + 2] = rim[2];
        data[at + 3] = Math.round(HALO_ALPHA * (1 + v / HALO));
        continue;
      }
      const above = dripField(seed, growth, dx, dy - 1.5) <= 0;
      const below = dripField(seed, growth, dx, dy + 1.5) <= 0;
      const lit = (above && v < EDGE && roll(seed, x, y) > 40) || (v > 0.55 && roll(seed, x, y) > 246);
      const colour = lit ? sheen : below ? deep : streak;
      const soft = Math.min(1, v / EDGE);
      const alpha = lit ? SHEEN_ALPHA : WATER_ALPHA + (CORE_ALPHA - WATER_ALPHA) * Math.min(1, v);
      data[at] = colour[0];
      data[at + 1] = colour[1];
      data[at + 2] = colour[2];
      data[at + 3] = Math.round(alpha * (0.55 + 0.45 * soft));
    }
  }
}

/**
 * The growths tried, as shares of the most a puddle may grow, largest first, when its full size would cross something.
 */
const FIT_SHARES: readonly number[] = [1, 0.75, 0.5, 0.3];
const FIT_REACH = 16;

/**
 * The largest growth at which a drip puddle under the feet, halo and all, crosses no blocked point (ADR-144's amendment of
 * 2026-11-01, decision 8): a wall, a prop's art, a table. Tried at the full growth, then smaller; 0 when even the smallest
 * would cross one, so no puddle starts there.
 *
 * @param blockedAt Whether a point, as an offset from the feet in world pixels, is blocked.
 * @param seed The puddle's seed.
 * @param most The most it may grow (`DRIP_PUDDLE_MAX_FILL` times the wetness, over the max fill).
 * @returns The growth, 0 to `most`.
 */
export function dripGrowthThatFits(blockedAt: (dx: number, dy: number) => boolean, seed: number, most: number): number {
  for (const share of FIT_SHARES) {
    const growth = most * share;
    let clear = true;
    for (let y = -FIT_REACH; y < FIT_REACH && clear; y += 1) {
      for (let x = -FIT_REACH; x < FIT_REACH; x += 1) {
        if (dripField(seed, growth, x + 0.5, y + 0.5) > -HALO && blockedAt(x + 0.5, y + 0.5)) {
          clear = false;
          break;
        }
      }
    }
    if (clear) {
      return growth;
    }
  }
  return 0;
}
